<template>
  <div class="profile">
    <NavBar titleTranslationPath="profileOverview.title" location commune />
    <div class="content">
      <CollapsiblePanel v-if="savedPersons && savedPersons.length > 0">
        <div slot="headline">
          <font-awesome-icon icon="id-badge" class="title-icon" />
          <NewTranslation path="profile.savedPersons.label" />
        </div>
        <template slot="body">
          <div class="user-list">
            <div
              class="user-entry"
              v-for="(person, index) in savedPersons"
              :class="{ 'is-open': personOpen === person.id }"
              :key="person.id"
            >
              <div class="user-head" @click="togglePerson(person.id)">
                {{
                  person.firstName || person.lastName ? `${person.firstName} ${person.lastName}` : `Person ${index + 1}`
                }}
                <div class="user-toggle">
                  <font-awesome-icon icon="chevron-down" />
                </div>
                <div
                  class="user-remove"
                  @click="removePerson(person.id)"
                  v-if="!person.isSelf && !isDeletingPerson(person.id)"
                >
                  <font-awesome-icon icon="trash" />
                </div>
                <Loader
                  style="display: inline-block;width: auto;margin: 0;position: absolute;right: 21px;top: 8px;transform: scale(0.5);"
                  v-if="isDeletingPerson(person.id)"
                ></Loader>
              </div>
              <div class="user-body">
                <!-- Gender Select -->
                <SelectField
                  :identifier="`person-${index}-salutation`"
                  label="profile.savedPersons.salutation.label"
                  v-model="person.salutation"
                  :options="salutationOptions"
                  :is-mandatory="true"
                  :validation-error="getPersonFieldError(person.id, 'salutation')"
                  v-on:focus="removePersonFieldError(person.id, 'salutation')"
                >
                </SelectField>
                <!-- Title Select -->
                <SelectField
                  :identifier="`person-${index}-title`"
                  label="profile.savedPersons.title.label"
                  v-model="person.title"
                  :options="titleOptions"
                  :is-mandatory="false"
                  :validation-error="getPersonFieldError(person.id, 'title')"
                  v-on:focus="removePersonFieldError(person.id, 'title')"
                >
                </SelectField>
                <TextField
                  :identifier="`person-${index}-firstName`"
                  label="profile.savedPersons.firstName.label"
                  placeholder="profile.savedPersons.firstName.placeholder"
                  v-model="person.firstName"
                  :is-mandatory="true"
                  :validation-error="getPersonFieldError(person.id, 'firstName')"
                  v-on:focus="removePersonFieldError(person.id, 'firstName')"
                >
                  <font-awesome-icon icon="user" slot="icon" />
                </TextField>
                <TextField
                  :identifier="`person-${index}-lastName`"
                  label="profile.savedPersons.lastName.label"
                  placeholder="profile.savedPersons.lastName.placeholder"
                  v-model="person.lastName"
                  :is-mandatory="true"
                  :validation-error="getPersonFieldError(person.id, 'lastName')"
                  v-on:focus="removePersonFieldError(person.id, 'lastName')"
                >
                  <font-awesome-icon icon="signature" slot="icon" />
                </TextField>
                <!-- Birthday -->
                <BirthdayField
                  :identifier="`person-${index}-birthday`"
                  label="profile.savedPersons.birthday.label"
                  placeholder="profile.savedPersons.birthday.placeholder"
                  v-model="person.birthday"
                  :has-info="true"
                  :is-local="false"
                  :validation-error="getPersonFieldError(person.id, 'birthday')"
                  v-on:focus="removePersonFieldError(person.id, 'birthday')"
                />
                <!-- Street and Number -->
                <TextField
                  :identifier="`person-${index}-address-streetAndNumber`"
                  label="profile.savedPersons.streetAndNumber.label"
                  placeholder="profile.savedPersons.streetAndNumber.placeholder"
                  v-model="person.addressStreetAndNumber"
                  :validation-error="getPersonFieldError(person.id, 'addressStreetAndNumber')"
                  v-on:focus="removePersonFieldError(person.id, 'addressStreetAndNumber')"
                >
                  <font-awesome-icon icon="road" slot="icon" />
                </TextField>
                <!-- Zip Code -->
                <TextField
                  :identifier="`person-${index}-address-zipCode`"
                  label="profile.savedPersons.zipCode.label"
                  placeholder="profile.savedPersons.zipCode.placeholder"
                  v-model="person.addressZip"
                  :validation-error="getPersonFieldError(person.id, 'addressZip')"
                  v-on:focus="removePersonFieldError(person.id, 'addressZip')"
                >
                  <font-awesome-icon icon="street-view" slot="icon" />
                </TextField>
                <!-- City -->
                <TextField
                  :identifier="`person-${index}-address-city`"
                  label="profile.savedPersons.city.label"
                  placeholder="profile.savedPersons.city.placeholder"
                  v-model="person.addressCity"
                  :validation-error="getPersonFieldError(person.id, 'addressCity')"
                  v-on:focus="removePersonFieldError(person.id, 'addressCity')"
                >
                  <font-awesome-icon icon="city" slot="icon" />
                </TextField>
                <!-- Country -->
                <SelectField
                  :identifier="`person-${index}-address-country`"
                  label="profile.savedPersons.country.label"
                  placeholder="profile.savedPersons.country.placeholder"
                  v-model="person.addressCountry"
                  :options="countryOptions"
                  :validation-error="getPersonFieldError(person.id, 'addressCountry')"
                  v-on:focus="removePersonFieldError(person.id, 'addressCountry')"
                >
                </SelectField>
                <!-- Nationality -->
                <SelectField
                  :identifier="`person-${index}-nationality`"
                  label="profile.savedPersons.nationality.label"
                  placeholder="profile.savedPersons.nationality.placeholder"
                  v-model="person.nationality"
                  :options="countryOptions"
                  :validation-error="getPersonFieldError(person.id, 'nationality')"
                  v-on:focus="removePersonFieldError(person.id, 'nationality')"
                >
                </SelectField>                
                <!-- Phone Number -->
                <TextField
                  :identifier="`person-${index}-phone`"
                  label="profile.savedPersons.cellphoneNumber.label"
                  placeholder="profile.savedPersons.cellphoneNumber.placeholder"
                  v-model="person.phone"
                  :validation-error="getPersonFieldError(person.id, 'phone')"
                  v-on:focus="removePersonFieldError(person.id, 'phone')"
                >
                  <font-awesome-icon icon="phone-alt" slot="icon" />
                </TextField>
                <!-- E-Mail -->
                <TextField
                  :identifier="`person-${index}-mail`"
                  label="profile.savedPersons.mail.label"
                  placeholder="profile.savedPersons.mail.placeholder"
                  v-model="person.mail"
                  :validation-error="getPersonFieldError(person.id, 'mail')"
                  v-on:focus="removePersonFieldError(person.id, 'mail')"
                >
                  <font-awesome-icon icon="envelope" slot="icon" />
                </TextField>

                <Button
                  v-if="!isSavingPerson(person.id)"
                  class="savebutton"
                  @click.native="savePersonChanges(person)"
                  path="profile.savedPersons.save.button"
                />
                <Loader v-if="isSavingPerson(person.id)" />
              </div>
            </div>
            <div v-if="isAddingSavedPerson" class="user-entry" :class="{ 'is-open': personOpen === null }">
              <div class="user-head" @click="togglePerson(null)">
                {{
                  newPerson.firstName || newPerson.lastName
                    ? `${newPerson.firstName} ${newPerson.lastName}`
                    : translate('profile.savedPersons.title')
                }}
                <div style="right: 0" class="user-remove" @click="removePerson(null)">
                  <font-awesome-icon icon="trash" />
                </div>
              </div>
              <div class="user-body">
                <SelectField
                  :identifier="`person-salutation`"
                  label="profile.savedPersons.salutation.label"
                  v-model="newPerson.salutation"
                  :options="salutationOptions"
                  :is-mandatory="true"
                  :validation-error="getPersonFieldError(null, 'salutation')"
                  v-on:focus="removePersonFieldError(null, 'salutation')"
                >
                </SelectField>
                <SelectField
                  :identifier="`person-title`"
                  label="profile.savedPersons.title.label"
                  v-model="newPerson.title"
                  :options="titleOptions"
                  :is-mandatory="false"
                  :validation-error="getPersonFieldError(null, 'title')"
                  v-on:focus="removePersonFieldError(null, 'title')"
                >
                </SelectField>
                <!-- First Name -->
                <TextField
                  :identifier="`person-firstName`"
                  label="profile.savedPersons.firstName.label"
                  placeholder="profile.savedPersons.firstName.placeholder"
                  v-model="newPerson.firstName"
                  :is-mandatory="true"
                  :validation-error="getPersonFieldError(null, 'firstName')"
                  v-on:focus="removePersonFieldError(null, 'firstName')"
                >
                  <font-awesome-icon icon="user" slot="icon" />
                </TextField>
                <!-- Last Name -->
                <TextField
                  :identifier="`person-lastName`"
                  label="profile.savedPersons.lastName.label"
                  placeholder="profile.savedPersons.lastName.placeholder"
                  v-model="newPerson.lastName"
                  :is-mandatory="true"
                  :validation-error="getPersonFieldError(null, 'lastName')"
                  v-on:focus="removePersonFieldError(null, 'lastName')"
                >
                  <font-awesome-icon icon="signature" slot="icon" />
                </TextField>
                <!-- Birthday -->
                <BirthdayField
                  :identifier="`person-birthday`"
                  label="profile.savedPersons.birthday.label"
                  placeholder="profile.savedPersons.birthday.placeholder"
                  v-model="newPerson.birthday"
                  :has-info="true"
                  :is-local="false"
                  :validation-error="getPersonFieldError(null, 'birthday')"
                  v-on:focus="removePersonFieldError(null, 'birthday')"
                />
                <TextField
                  :identifier="`person-address-streetAndNumber`"
                  label="profile.savedPersons.streetAndNumber.label"
                  placeholder="profile.savedPersons.streetAndNumber.placeholder"
                  v-model="newPerson.addressStreetAndNumber"
                  :validation-error="getPersonFieldError(null, 'addressStreetAndNumber')"
                  v-on:focus="removePersonFieldError(null, 'addressStreetAndNumber')"
                >
                  <font-awesome-icon icon="road" slot="icon" />
                </TextField>
                <TextField
                  :identifier="`person-address-zipCode`"
                  label="profile.savedPersons.zipCode.label"
                  placeholder="profile.savedPersons.zipCode.placeholder"
                  v-model="newPerson.addressZip"
                  :validation-error="getPersonFieldError(null, 'addressZip')"
                  v-on:focus="removePersonFieldError(null, 'addressZip')"
                >
                  <font-awesome-icon icon="street-view" slot="icon" />
                </TextField>
                <TextField
                  :identifier="`person-address-city`"
                  label="profile.savedPersons.city.label"
                  placeholder="profile.savedPersons.city.placeholder"
                  v-model="newPerson.addressCity"
                  :validation-error="getPersonFieldError(null, 'addressCity')"
                  v-on:focus="removePersonFieldError(null, 'addressCity')"
                >
                  <font-awesome-icon icon="city" slot="icon" />
                </TextField>
                <!-- Country Select -->
                <SelectField
                  :identifier="`person-address-country`"
                  label="profile.savedPersons.country.label"
                  placeholder="profile.savedPersons.country.placeholder"
                  v-model="newPerson.addressCountry"
                  :options="countryOptions"
                  :validation-error="getPersonFieldError(null, 'addressCountry')"
                  v-on:focus="removePersonFieldError(null, 'addressCountry')"
                >
                </SelectField>
                <!-- Nationality Select -->
                <SelectField
                  :identifier="`person-nationality`"
                  label="profile.savedPersons.nationality.label"
                  placeholder="profile.savedPersons.nationality.placeholder"
                  v-model="newPerson.nationality"
                  :options="countryOptions"
                  :validation-error="getPersonFieldError(null, 'nationality')"
                  v-on:focus="removePersonFieldError(null, 'nationality')"
                >
                </SelectField>                
                <TextField
                  :identifier="`person-phone`"
                  label="profile.savedPersons.cellphoneNumber.label"
                  placeholder="profile.savedPersons.cellphoneNumber.placeholder"
                  v-model="newPerson.phone"
                  :validation-error="getPersonFieldError(null, 'phone')"
                  v-on:focus="removePersonFieldError(null, 'phone')"
                >
                  <font-awesome-icon icon="phone-alt" slot="icon" />
                </TextField>
                <TextField
                  :identifier="`person-mail`"
                  label="profile.savedPersons.mail.label"
                  placeholder="profile.savedPersons.mail.placeholder"
                  v-model="newPerson.mail"
                  :validation-error="getPersonFieldError(null, 'mail')"
                  v-on:focus="removePersonFieldError(null, 'mail')"
                >
                  <font-awesome-icon icon="envelope" slot="icon" />
                </TextField>

                <Button
                  v-if="!isSavingPerson(null)"
                  class="savebutton"
                  @click.native="addPerson()"
                  path="profile.savedPersons.save.button"
                />
                <Loader v-if="isSavingPerson(null)" />
              </div>
            </div>
            <div v-if="!isAddingSavedPerson" class="user-head" style="padding: 0;">
              <Button
                small
                style="justify-content: center; font-size: 1rem;"
                v-on:click.native="openSavedPersonForm()"
                path="profile.savedPersons.add.button"
              />
            </div>
          </div>
        </template>
      </CollapsiblePanel>
      <!-- Annual -->
      <CollapsiblePanel v-if="annualCandidates && annualCandidates.length > 0">
        <div slot="headline">
          <font-awesome-icon icon="address-card" class="title-icon" />
          <NewTranslation path="profile.cumulativeCards.label" />
        </div>
        <section slot="body" class="cumulative-display" v-for="(candidate, index) in annualCandidates" :key="index">
          <div class="commune-title">{{ candidate.communeTitle }}</div>
          <div class="text" v-if="candidate.limitType === 'price'">
            <NewTranslation
              path="profile.cumulativeCards.price.description"
              :params="{
                currentAmount: candidate.limitPriceAmount - candidate.missingPriceAmount,
                limitAmount: candidate.limitPriceAmount,
              }"
            />
          </div>
          <div class="text" v-else>
            <NewTranslation
              path="profile.cumulativeCards.days.description"
              :params="{
                currentDays: candidate.limitDays - candidate.missingDays,
                limitDays: candidate.limitDays,
              }"
            />
          </div>
          <div class="progress-bar">
            <div
              class="progress-value"
              v-if="candidate.limitType === 'price'"
              :style="{
                width:
                  ((candidate.limitPriceAmount - candidate.missingPriceAmount) / candidate.limitPriceAmount) * 100 +
                  '%',
              }"
            ></div>
            <div
              class="progress-value"
              v-else
              :style="{ width: ((candidate.limitDays - candidate.missingDays) / candidate.limitDays) * 100 + '%' }"
            ></div>
          </div>
        </section>
      </CollapsiblePanel>
      <!-- Preferences -->
      <div class="addCards" @click="onTransferClick()">
        <NewTranslation path="profile.addCards.label" />
        <font-awesome-icon icon="plus-circle" class="plus-icon" />
      </div>
      <!-- Favorites -->
      <CollapsiblePanel v-if="hasFavoritePlaces">
        <div slot="headline">
          <font-awesome-icon icon="bookmark" class="title-icon" />
          <NewTranslation path="profile.favorites.label" />
        </div>
        <template slot="body">
          <section class="favorites-entry" v-for="benefit in favorites" :key="benefit.id">
            <img class="favorites-image" :src="getBenefitImageUrl(benefit)" />
            <router-link
              class="favorites-text"
              :to="{
                name: $namedRoutes.benefitsDetail,
                params: {
                  pageData: benefit,
                },
              }"
            >
              <div class="favorites-text-name">{{ benefit.venueTitle }}</div>
              <div class="favorites-text-location">{{ benefit.benefitCommune }}</div>
            </router-link>
            <div class="favorites-remove" @click="removeFavorite(benefit)">
              <font-awesome-icon icon="trash" />
            </div>
          </section>
          <section>
            <Button class="deletebutton" center="center" @click.native="removeFavoriteAll()">
              <NewTranslation path="profile.favorites.removeAll.button" />
              <font-awesome-icon icon="trash" slot="icon" />
            </Button>
          </section>
        </template>
      </CollapsiblePanel>
      <!-- Settings -->
      <CollapsiblePanel>
        <div slot="headline">
          <font-awesome-icon icon="cog" class="title-icon" />
          <NewTranslation path="profile.settings.label" />
        </div>
        <template slot="body">
          <section :class="{ 'is-open-password': passwordOpen }">
            <CollapsiblePanel class="nested-panel">
              <NewTranslation path="profile.settings.changePassword.label" slot="headline" />
              <section slot="body">
                <!-- Old Password -->
                <TextField
                  v-model="passwordData.oldPassword"
                  label="profile.settings.changePassword.oldPassword.label"
                  identifier="field-oldpassword"
                  type="password"
                  placeholder="profile.settings.changePassword.oldPassword.placeholder"
                  required="required"
                >
                  <font-awesome-icon icon="fingerprint" class="input-icon" slot="icon" />
                </TextField>
                <!-- New Password -->
                <TextField
                  v-model="passwordData.newPassword"
                  label="profile.settings.changePassword.newPassword.label"
                  identifier="field-newpassword"
                  type="password"
                  placeholder="profile.settings.changePassword.newPassword.placeholder"
                  required="required"
                >
                  <font-awesome-icon icon="fingerprint" class="input-icon" slot="icon" />
                </TextField>
                <!-- Repeat new Password -->
                <TextField
                  v-model="passwordData.newPasswordRepeat"
                  label="profile.settings.changePassword.repeatPassword.label"
                  identifier="field-newpasswordrepeat"
                  type="password"
                  placeholder="profile.settings.changePassword.repeatPassword.placeholder"
                  required="required"
                >
                  <font-awesome-icon icon="fingerprint" class="input-icon" slot="icon" />
                </TextField>
                <!-- Change Button -->
                <Button
                  class="form-button"
                  @click.native="changePassword"
                  center="center"
                  path="profile.settings.changePassword.button"
                />
              </section>
            </CollapsiblePanel>
          </section>
          <section>
            <Button class="logoutbutton" @click.native="logout" center="center" path="profile.settings.logout.button" />
          </section>
          <section>
            <Button
              @click.native="open('AccountDeletion')"
              center="center"
              color="gray"
              path="profile.settings.deleteAccount.button"
            />
            <div class="text button-hint">
              <NewTranslation path="profile.settings.deleteAccount.hint" />
            </div>
          </section>
        </template>
      </CollapsiblePanel>
    </div>
    <TabBar />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import NavBar from '@/components/NavBar.vue';
import TabBar from '@/components/TabBar.vue';
import Button from '@/components/Button.vue';
import SelectField from '@/components/form/SelectField';
import TextField from '@/components/form/TextField';
import BirthdayField from '@/components/form/BirthdayField';
import Loader from '@/components/Loader';
import NewTranslation from '@/components/i18n/NewTranslation';
import i18next from 'i18next';
import CollapsiblePanel from '@/components/CollapsablePanel';
import CheckboxField from '@/components/form/CheckboxField';
import UserApi from '@/app/api/UserApi';
import RemoteAssets from '@/app/utils/RemoteAssets';
import AuthRedirect from '@/app/utils/AuthRedirect';
import { NamedRoutes } from '@/routes/routes';

export default {
  components: {
    CheckboxField,
    CollapsiblePanel,
    NewTranslation,
    NavBar,
    TabBar,
    Button,
    SelectField,
    TextField,
    BirthdayField,
    Loader,
  },
  props: ['pagedata'],
  data() {
    return {
      taxId: '',
      tabOpen: '',
      passwordOpen: false,
      personOpen: -1,
      annualCandidates: [],
      savedPersons: [],
      savedPersonErrors: {},
      personLoaders: {},
      isAddingSavedPerson: false,
      newPerson: {
        salutation: 'MR',
        title: '',
        firstName: '',
        lastName: '',
        birthday: null,
        addressStreetAndNumber: '',
        addressZip: '',
        addressCity: '',
        addressCountry: '',
        nationality: '',
        phone: '',
        mail: '',
      },
      passwordData: {
        oldPassword: '',
        newPassword: '',
        newPasswordRepeat: '',
      },
    };
  },
  mounted() {
    if (this.pagedata && this.pagedata.tabOpen) this.tabOpen = this.pagedata.tabOpen;

    UserApi.getAnnualCandidates().then(candidates => {
      this.annualCandidates = candidates;
    });
    this.fetchSavedPersons();
  },
  created() {
    if (this.$route.query.transferCode) {
      this.taxId = this.$route.query.transferCode;
      this.initiateTransfer(this.$route.query.transferCode);
    }
  },
  methods: {
    openSavedPersonForm() {
      this.isAddingSavedPerson = true;
      this.personOpen = null;
      setTimeout(() => {
        // wait for form to render
        const element = document.querySelector('.user-entry.is-open');
        element.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }, 10);
    },
    fetchSavedPersons() {
      this.userSavedPersons().then(people => (this.savedPersons = people));
    },
    toggleTab(tabname) {
      if (this.tabOpen === tabname) this.tabOpen = '';
      else this.tabOpen = tabname;
    },
    togglePerson(index) {
      if (this.personOpen === index) this.personOpen = -1;
      else this.personOpen = index;
    },
    isDeletingPerson(personId) {
      return (this.personLoaders[personId] && this.personLoaders[personId].isDeletingPerson) || false;
    },
    isSavingPerson(personId) {
      return (this.personLoaders[personId] && this.personLoaders[personId].isSavingPerson) || false;
    },
    getBenefitImageUrl(benefit) {
      return RemoteAssets.benefitImageUrl(benefit.venueImage);
    },
    savePersonChanges(person) {
      const { id } = person;
      delete this.savedPersonErrors[id];

      const loaders = { ...this.personLoaders };
      loaders[id] = { isSavingPerson: true, isDeletingPerson: false };
      this.personLoaders = loaders;

      // noinspection JSCheckFunctionSignatures
      UserApi.updateSavedPerson(person)
        .then(() => {
          this.showError(
            i18next.t('profile.savedPersons.successDialog.body'),
            i18next.t('profile.savedPersons.successDialog.body')
          );
          this.personOpen = -1;
        })
        .catch(e => {
          if (e && e.response && e.response.status === 422) {
            const errors = { ...this.savedPersonErrors };
            errors[id] = e.response.data.errors;
            this.savedPersonErrors = errors;

            setTimeout(() => {
              const element = document.querySelector('.has-errors');
              if (element) {
                element.scrollIntoView({
                  behavior: 'smooth',
                });
              }
            }, 100);
          } else {
            this.showError(
              i18next.t('profile.savedPersons.errorDialog.body'),
              i18next.t('profile.savedPersons.errorDialog.title')
            );
          }
        })
        .then(() => {
          const loaders = { ...this.personLoaders };
          loaders[id] = { isSavingPerson: false, isDeletingPerson: false };
          this.personLoaders = loaders;
        });
    },
    addPerson() {
      const id = null;
      delete this.savedPersonErrors[id];

      const loaders = { ...this.personLoaders };
      loaders[id] = { isSavingPerson: true, isDeletingPerson: false };
      this.personLoaders = loaders;

      UserApi.createSavedPerson(this.newPerson)
        .then(() => {
          this.showError(
            i18next.t('profile.savedPersons.successDialog.body'),
            i18next.t('profile.savedPersons.successDialog.title')
          );
          this.personOpen = -1;
          this.isAddingSavedPerson = false;
          this.newPerson = {
            salutation: 'MR',
            title: '',
            firstName: '',
            lastName: '',
            birthday: null,
            addressStreetAndNumber: '',
            addressZip: '',
            addressCity: '',
            addressCountry: '',
            nationality: '',
            phone: '',
            mail: '',
          };
          this.fetchSavedPersons();
        })
        .catch(e => {
          if (e && e.response && e.response.status === 422) {
            const errors = { ...this.savedPersonErrors };
            errors[id] = e.response.data.errors;
            this.savedPersonErrors = errors;

            setTimeout(() => {
              const element = document.querySelector('.has-errors');
              if (element) {
                element.scrollIntoView({
                  behavior: 'smooth',
                });
              }
            }, 100);
          } else {
            this.showError(
              i18next.t('profile.savedPersons.errorDialog.body'),
              i18next.t('profile.savedPersons.errorDialog.title')
            );
          }
        })
        .then(() => {
          const loaders = { ...this.personLoaders };
          loaders[id] = { isSavingPerson: false, isDeletingPerson: false };
          this.personLoaders = loaders;
        });
    },
    removePerson(id) {
      if (id === null) {
        this.newPerson = {
          salutation: 'MR',
          title: '',
          firstName: '',
          lastName: '',
          birthday: null,
          addressStreetAndNumber: '',
          addressZip: '',
          addressCity: '',
          addressCountry: '',
          nationality: '',
          phone: '',
          mail: '',
        };
        this.personOpen = -1;
        this.isAddingSavedPerson = false;
        return;
      }
      this.personOpen = -1;

      const loaders = { ...this.personLoaders };
      loaders[id] = { isSavingPerson: true, isDeletingPerson: true };
      this.personLoaders = loaders;

      UserApi.deleteSavedPerson(id)
        .then(() => {
          this.showError(
            i18next.t('profile.settings.deleteAccount.request.success.dialog.body'),
            i18next.t('profile.settings.deleteAccount.request.success.dialog.title')
          );
          this.fetchSavedPersons();
        })
        .catch(() => {
          this.showError(
            i18next.t('profile.settings.deleteAccount.request.error.dialog.body'),
            i18next.t('profile.settings.deleteAccount.request.error.dialog.title')
          );
        })
        .catch(() => {
          const loaders = { ...this.personLoaders };
          loaders[id] = { isSavingPerson: false, isDeletingPerson: false };
          this.personLoaders = loaders;
        });
    },
    translate(path, params) {
      return i18next.t(path, params);
    },
    changePassword() {
      const fields = document.querySelectorAll('.profile-settings-password input[required]');
      let invalidField = false;
      for (let i = 0; i < fields.length; i++) {
        if (!fields[i].reportValidity()) {
          invalidField = true;
          return;
        }
      }

      if (this.passwordData.newPassword !== this.passwordData.newPasswordRepeat) {
        this.showError(
          i18next.t('profile.settings.changePassword.validation.dialog.body'),
          i18next.t('profile.settings.changePassword.validation.dialog.title')
        );
        return;
      }

      // @DEBUG
      axios
        .patch(
          process.env.VUE_APP_API_BASEURL + '/account',
          {
            password: this.passwordData.newPassword,
          },
          { withCredentials: true }
        )
        .then(response => {
          this.clearPasswordFields();
          this.showError(
            i18next.t('profile.settings.changePassword.request.success.dialog.body'),
            i18next.t('profile.settings.changePassword.request.success.dialog.title')
          );
        })
        .catch(error => {
          this.showError(error, i18next.t('profile.settings.changePassword.request.error.dialog.title'));
        });
    },
    clearPasswordFields() {
      this.passwordData.oldPassword = '';
      this.passwordData.newPassword = '';
      this.passwordData.newPasswordRepeat = '';
    },
    logout() {
      // @DEBUG
      axios
        .delete(process.env.VUE_APP_API_BASEURL + '/session', { withCredentials: true })
        .then(response => {
          this.$store.dispatch('logoutUser');
          this.$router.push({ name: this.$namedRoutes.splash });
        })
        .catch(error => {
          this.showError(error, i18next.t('profile.settings.logout.request.error.dialog.title'));
        });
    },
    open(overlayname) {
      this.$store.commit('openOverlay', overlayname);
    },
    showError(message, title) {
      this.$store.commit('openOverlay', {
        overlayName: 'Info',
        overlayData: {
          title,
          message,
        },
      });
    },
    removeFavorite(benefit) {
      this.$store.dispatch('removeFavorite', benefit);
    },
    removeFavoriteAll() {
      this.$store.dispatch('removeFavoriteAll');
    },
    togglePreference(preference, value) {
      if (value) {
        this.$store.dispatch('enablePreference', preference);
      } else {
        this.$store.dispatch('disablePreference', preference);
      }
    },
    getPersonFieldError(personId, fieldName) {
      if (!this.savedPersonErrors[personId]) {
        return null;
      }
      const error = this.savedPersonErrors[personId].find(e => e.property === fieldName);
      return error || null;
    },
    removePersonFieldError(personId, fieldName) {
      if (!this.savedPersonErrors[personId]) {
        return;
      }
      let index = 0;
      this.savedPersonErrors[personId].forEach((err, i) => {
        if (err.property === fieldName) {
          index = i;
        }
      });
      this.savedPersonErrors[personId].splice(index, 1);
    },
    onTransferClick() {
      const taxId = this.taxId;
      //if not logged in: request login
      if (Boolean(this.loggedInUser)) {
        this.initiateTransfer(taxId);
      } else {
        AuthRedirect.setRedirect({
          name: NamedRoutes.splash,
          query: { transferCode: taxId },
          params: { communeId: this.$route.params.communeId },
        });
        this.$router.push({
          name: NamedRoutes.login,
        });
      }
    },
    initiateTransfer(taxId) {
      this.$store.commit('openOverlay', {
        overlayName: 'EnterTransferCode',
        overlayData: {
          taxId: this.taxId,
        },
      });
      this.taxId = '';
    },
    ...mapActions(['userSavedPersons']),
  },
  computed: {
    ...mapGetters(['savedPeople', 'loggedInUser', 'favorites', 'preferencesByCategory']),
    hasFavoritePlaces() {
      return this.favorites.length > 0;
    },
    salutationOptions() {
      return [
        { value: '', label: '' },
        { value: 'MR', label: i18next.t('salutation.MR') },
        { value: 'MRS', label: i18next.t('salutation.MRS') },
        { value: 'NONE', label: i18next.t('salutation.NONE') },
      ];
    },
    titleOptions() {
      return [
        { value: '', label: '' },
        { value: 'Dr.', label: 'Dr.' },
        { value: 'Prof.', label: 'Prof.' },
        { value: 'Prof. Dr.', label: 'Prof. Dr.' },
        { value: 'Ing.', label: 'Ing.' },
        { value: 'Dipl.-Ing.', label: 'Dipl.-Ing.' },
        { value: 'Mag.', label: 'Mag.' },
      ];
    },
    countryOptions() {
      return [
        { value: '', label: 'Ihr Land' },
        { value: 'AF', label: 'Afghanistan' },
        { value: 'EG', label: 'Ägypten' },
        { value: 'AX', label: 'Åland' },
        { value: 'AL', label: 'Albanien' },
        { value: 'DZ', label: 'Algerien' },
        { value: 'AS', label: 'Amerikanisch-Samoa' },
        { value: 'VI', label: 'Amerikanische Jungferninseln' },
        { value: 'AD', label: 'Andorra' },
        { value: 'AO', label: 'Angola' },
        { value: 'AI', label: 'Anguilla' },
        { value: 'AQ', label: 'Antarktis' },
        { value: 'AG', label: 'Antigua und Barbuda' },
        { value: 'GQ', label: 'Äquatorialguinea' },
        { value: 'AR', label: 'Argentinien' },
        { value: 'AM', label: 'Armenien' },
        { value: 'AW', label: 'Aruba' },
        { value: 'AC', label: 'Ascension' },
        { value: 'AZ', label: 'Aserbaidschan' },
        { value: 'ET', label: 'Äthiopien' },
        { value: 'AU', label: 'Australien' },
        { value: 'BS', label: 'Bahamas' },
        { value: 'BH', label: 'Bahrain' },
        { value: 'BD', label: 'Bangladesch' },
        { value: 'BB', label: 'Barbados' },
        { value: 'BY', label: 'Belarus (Weißrussland)' },
        { value: 'BE', label: 'Belgien' },
        { value: 'BZ', label: 'Belize' },
        { value: 'BJ', label: 'Benin' },
        { value: 'BM', label: 'Bermuda' },
        { value: 'BT', label: 'Bhutan' },
        { value: 'BO', label: 'Bolivien' },
        { value: 'BA', label: 'Bosnien und Herzegowina' },
        { value: 'BW', label: 'Botswana' },
        { value: 'BV', label: 'Bouvetinsel' },
        { value: 'BR', label: 'Brasilien' },
        { value: 'VG', label: 'Britische Jungferninseln' },
        { value: 'IO', label: 'Britisches Territorium im Indischen Ozean' },
        { value: 'BN', label: 'Brunei Darussalam' },
        { value: 'BG', label: 'Bulgarien' },
        { value: 'BF', label: 'Burkina Faso' },
        { value: 'BI', label: 'Burundi' },
        { value: 'EA', label: 'Ceuta, Melilla' },
        { value: 'CL', label: 'Chile' },
        { value: 'CN', label: 'Volksrepublik China' },
        { value: 'CP', label: 'Clipperton' },
        { value: 'CK', label: 'Cookinseln' },
        { value: 'CR', label: 'Costa Rica' },
        { value: 'CI', label: "Côte d'Ivoire (Elfenbeinküste)" },
        { value: 'DK', label: 'Dänemark' },
        { value: 'DE', label: 'Deutschland' },
        { value: 'DG', label: 'Diego Garcia' },
        { value: 'DM', label: 'Dominica' },
        { value: 'DO', label: 'Dominikanische Republik' },
        { value: 'DJ', label: 'Dschibuti' },
        { value: 'EC', label: 'Ecuador' },
        { value: 'SV', label: 'El Salvador' },
        { value: 'ER', label: 'Eritrea' },
        { value: 'EE', label: 'Estland' },
        { value: 'FK', label: 'Falklandinseln' },
        { value: 'FO', label: 'Färöer' },
        { value: 'FJ', label: 'Fidschi' },
        { value: 'FI', label: 'Finnland' },
        { value: 'FR', label: 'Frankreich' },
        { value: 'GF', label: 'Französisch-Guayana' },
        { value: 'PF', label: 'Französisch-Polynesien' },
        { value: 'GA', label: 'Gabun' },
        { value: 'GM', label: 'Gambia' },
        { value: 'GE', label: 'Georgien' },
        { value: 'GH', label: 'Ghana' },
        { value: 'GI', label: 'Gibraltar' },
        { value: 'GD', label: 'Grenada' },
        { value: 'GR', label: 'Griechenland' },
        { value: 'GL', label: 'Grönland' },
        { value: 'GB', label: 'Großbritannien' },
        { value: 'GP', label: 'Guadeloupe' },
        { value: 'GU', label: 'Guam' },
        { value: 'GT', label: 'Guatemala' },
        { value: 'GG', label: 'Guernsey (Kanalinsel)' },
        { value: 'GN', label: 'Guinea' },
        { value: 'GW', label: 'Guinea-Bissau' },
        { value: 'GY', label: 'Guyana' },
        { value: 'HT', label: 'Haiti' },
        { value: 'HM', label: 'Heard- und McDonald-Inseln' },
        { value: 'HN', label: 'Honduras' },
        { value: 'HK', label: 'Hongkong' },
        { value: 'IN', label: 'Indien' },
        { value: 'ID', label: 'Indonesien' },
        { value: 'IM', label: 'Insel Man' },
        { value: 'IQ', label: 'Irak' },
        { value: 'IR', label: 'Iran' },
        { value: 'IE', label: 'Irland' },
        { value: 'IS', label: 'Island' },
        { value: 'IL', label: 'Israel' },
        { value: 'IT', label: 'Italien' },
        { value: 'JM', label: 'Jamaika' },
        { value: 'JP', label: 'Japan' },
        { value: 'YE', label: 'Jemen' },
        { value: 'JE', label: 'Jersey (Kanalinsel)' },
        { value: 'JO', label: 'Jordanien' },
        { value: 'KY', label: 'Kaimaninseln' },
        { value: 'KH', label: 'Kambodscha' },
        { value: 'CM', label: 'Kamerun' },
        { value: 'CA', label: 'Kanada' },
        { value: 'IC', label: 'Kanarische Inseln' },
        { value: 'CV', label: 'Kap Verde' },
        { value: 'KZ', label: 'Kasachstan' },
        { value: 'QA', label: 'Katar' },
        { value: 'KE', label: 'Kenia' },
        { value: 'KG', label: 'Kirgisistan' },
        { value: 'KI', label: 'Kiribati' },
        { value: 'CC', label: 'Kokosinseln' },
        { value: 'CO', label: 'Kolumbien' },
        { value: 'KM', label: 'Komoren' },
        { value: 'CD', label: 'Demokratische Republik Kongo' },
        { value: 'KP', label: 'Demokratische Volksrepublik Korea (Nordkorea)' },
        { value: 'KR', label: 'Republik Korea (Südkorea)' },
        { value: 'HR', label: 'Kroatien' },
        { value: 'CU', label: 'Kuba' },
        { value: 'KW', label: 'Kuwait' },
        { value: 'LA', label: 'Laos' },
        { value: 'LS', label: 'Lesotho' },
        { value: 'LV', label: 'Lettland' },
        { value: 'LB', label: 'Libanon' },
        { value: 'LR', label: 'Liberia' },
        { value: 'LY', label: 'Libyen' },
        { value: 'LI', label: 'Liechtenstein' },
        { value: 'LT', label: 'Litauen' },
        { value: 'LU', label: 'Luxemburg' },
        { value: 'MO', label: 'Macao' },
        { value: 'MG', label: 'Madagaskar' },
        { value: 'MW', label: 'Malawi' },
        { value: 'MY', label: 'Malaysia' },
        { value: 'MV', label: 'Malediven' },
        { value: 'ML', label: 'Mali' },
        { value: 'MT', label: 'Malta' },
        { value: 'MA', label: 'Marokko' },
        { value: 'MH', label: 'Marshallinseln' },
        { value: 'MQ', label: 'Martinique' },
        { value: 'MR', label: 'Mauretanien' },
        { value: 'MU', label: 'Mauritius' },
        { value: 'YT', label: 'Mayotte' },
        { value: 'MK', label: 'Mazedonien' },
        { value: 'MX', label: 'Mexiko' },
        { value: 'FM', label: 'Mikronesien' },
        { value: 'MD', label: 'Moldawien (Republik Moldau)' },
        { value: 'MC', label: 'Monaco' },
        { value: 'MN', label: 'Mongolei' },
        { value: 'ME', label: 'Montenegro' },
        { value: 'MS', label: 'Montserrat' },
        { value: 'MZ', label: 'Mosambik' },
        { value: 'MM', label: 'Myanmar (Burma)' },
        { value: 'NA', label: 'Namibia' },
        { value: 'NR', label: 'Nauru' },
        { value: 'NP', label: 'Nepal' },
        { value: 'NC', label: 'Neukaledonien' },
        { value: 'NZ', label: 'Neuseeland' },
        { value: 'NI', label: 'Nicaragua' },
        { value: 'NL', label: 'Niederlande' },
        { value: 'AN', label: 'Niederländische Antillen' },
        { value: 'NE', label: 'Niger' },
        { value: 'NG', label: 'Nigeria' },
        { value: 'NU', label: 'Niue' },
        { value: 'MP', label: 'Nördliche Marianen' },
        { value: 'NF', label: 'Norfolkinsel' },
        { value: 'NO', label: 'Norwegen' },
        { value: 'OM', label: 'Oman' },
        { value: 'XO', label: 'Orbit' },
        { value: 'AT', label: 'Österreich' },
        { value: 'TL', label: 'Osttimor (Timor-Leste)' },
        { value: 'PK', label: 'Pakistan' },
        { value: 'PS', label: 'Palästinensische Autonomiegebiete' },
        { value: 'PW', label: 'Palau' },
        { value: 'PA', label: 'Panama' },
        { value: 'PG', label: 'Papua-Neuguinea' },
        { value: 'PY', label: 'Paraguay' },
        { value: 'PE', label: 'Peru' },
        { value: 'PH', label: 'Philippinen' },
        { value: 'PN', label: 'Pitcairninseln' },
        { value: 'PL', label: 'Polen' },
        { value: 'PT', label: 'Portugal' },
        { value: 'PR', label: 'Puerto Rico' },
        { value: 'TW', label: 'Republik China (Taiwan)' },
        { value: 'CG', label: 'Republik Kongo' },
        { value: 'RE', label: 'Réunion' },
        { value: 'RW', label: 'Ruanda' },
        { value: 'RO', label: 'Rumänien' },
        { value: 'RU', label: 'Russische Föderation' },
        { value: 'BL', label: 'Saint-Barthélemy' },
        { value: 'MF', label: 'Saint-Martin' },
        { value: 'SB', label: 'Salomonen' },
        { value: 'ZM', label: 'Sambia' },
        { value: 'WS', label: 'Samoa' },
        { value: 'SM', label: 'San Marino' },
        { value: 'ST', label: 'São Tomé und Príncipe' },
        { value: 'SA', label: 'Saudi-Arabien' },
        { value: 'SE', label: 'Schweden' },
        { value: 'CH', label: 'Schweiz' },
        { value: 'SN', label: 'Senegal' },
        { value: 'RS', label: 'Serbien' },
        { value: 'SC', label: 'Seychellen' },
        { value: 'SL', label: 'Sierra Leone' },
        { value: 'ZW', label: 'Simbabwe' },
        { value: 'SG', label: 'Singapur' },
        { value: 'SK', label: 'Slowakei' },
        { value: 'SI', label: 'Slowenien' },
        { value: 'SO', label: 'Somalia' },
        { value: 'ES', label: 'Spanien' },
        { value: 'LK', label: 'Sri Lanka' },
        { value: 'SH', label: 'St. Helena' },
        { value: 'KN', label: 'St. Kitts und Nevis' },
        { value: 'LC', label: 'St. Lucia' },
        { value: 'PM', label: 'Saint-Pierre und Miquelon' },
        { value: 'VC', label: 'St. Vincent und die Grenadinen' },
        { value: 'ZA', label: 'Südafrika' },
        { value: 'SD', label: 'Sudan' },
        { value: 'GS', label: 'Südgeorgien und die Südlichen Sandwichinseln' },
        { value: 'SR', label: 'Suriname' },
        { value: 'SJ', label: 'Svalbard und Jan Mayen' },
        { value: 'SZ', label: 'Swasiland' },
        { value: 'SY', label: 'Syrien' },
        { value: 'TJ', label: 'Tadschikistan' },
        { value: 'TZ', label: 'Tansania' },
        { value: 'TH', label: 'Thailand' },
        { value: 'TG', label: 'Togo' },
        { value: 'TK', label: 'Tokelau' },
        { value: 'TO', label: 'Tonga' },
        { value: 'TT', label: 'Trinidad und Tobago' },
        { value: 'TA', label: 'Tristan da Cunha' },
        { value: 'TD', label: 'Tschad' },
        { value: 'CZ', label: 'Tschechische Republik' },
        { value: 'TN', label: 'Tunesien' },
        { value: 'TR', label: 'Türkei' },
        { value: 'TM', label: 'Turkmenistan' },
        { value: 'TC', label: 'Turks- und Caicosinseln' },
        { value: 'TV', label: 'Tuvalu' },
        { value: 'UG', label: 'Uganda' },
        { value: 'UA', label: 'Ukraine' },
        { value: 'HU', label: 'Ungarn' },
        { value: 'UY', label: 'Uruguay' },
        { value: 'UZ', label: 'Usbekistan' },
        { value: 'VU', label: 'Vanuatu' },
        { value: 'VA', label: 'Vatikanstadt' },
        { value: 'VE', label: 'Venezuela' },
        { value: 'AE', label: 'Vereinigte Arabische Emirate' },
        { value: 'US', label: 'Vereinigte Staaten von Amerika (USA)' },
        { value: 'GB', label: 'Vereinigtes Königreich Großbritannien und Nordirland' },
        { value: 'VN', label: 'Vietnam' },
        { value: 'WF', label: 'Wallis und Futuna' },
        { value: 'CX', label: 'Weihnachtsinsel' },
        { value: 'EH', label: 'Westsahara' },
        { value: 'CF', label: 'Zentralafrikanische Republik' },
        { value: 'CY', label: 'Zypern' },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.title-icon {
  font-size: 24px;
  margin-right: 20px;
}
.input-icon {
  font-size: 15px;
}
section {
  padding-bottom: 20px;
  padding-top: 20px;

  & + section {
    border-top: 1px solid #f4f4f6;
  }
}

.headline {
  display: block;
  font-size: 1.25rem;
  margin-bottom: 20px;
}

.preferences {
  .field {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.nested-panel {
  margin: 0;
}

.form-button {
  margin-top: 20px;
}

.button-hint {
  margin-top: 10px;
  font-size: 14px;
}

.addCards {
  border-radius: 10px;
  background-color: #1db2d1;
  padding: 20px;
  color: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  max-width: 440px;
  margin: 0 auto;

  display: flex;
  justify-content: space-between;

  .plus-icon {
    font-size: 19px;
  }
}
</style>

<style lang="sass" scoped>
.content
	padding: 20px 20px 100px
	background-color: #F3F7F8
	min-height: calc(100vh - 92px)
.profile
	.commune-title
		font-size: 1.2rem
		margin-bottom: .8rem

	.savebutton
		margin-top: 20px


	&-tab-preferences
		.field
			flex-direction: row
			justify-content: space-between
			align-items: center
			input[type="checkbox"]
				-webkit-appearance: none
				padding: 0
				width: 35px
				height: 20px
				display: block
				border-radius: 20px
				background-color: #454F63
				border: none
				position: relative
				&:before
					content: ""
					display: block
					border-radius: 18px
					width: 18px
					height: 18px
					background-color: #fff
					position: absolute
					left: 1px
					top: 1px
			input[type="checkbox"]:checked
				background-color: #1DB2D1
				&:before
					left: auto
					right: 1px
	&-tab-settings
		section
			color: #454F63
			a
				color: #1DB2D1
		.changepassword
			margin-top: 20px
	&-settings
		&-head
			position: relative
		&-toggle
			width: 20px
			height: 20px
			display: flex
			justify-content: center
			align-items: center
			border-radius: 20px
			border: 1px solid #1DB2D1
			position: absolute
			top: 2px
			right: 0px
			svg
				color: #1DB2D1
				width: 10px
				.is-open-password &
					transform: rotate(180deg)
		&-body
			display: none
			.is-open-password &
				display: block

	.favorites,
	.coupons
		&-entry
			display: flex
			position: relative
		&-image
			width: 40px
			height: 40px
			border-radius: 40px
		&-text
			color: #454F63
			margin-left: 10px
			line-height: 1
			max-width: calc(100% - 80px)
			&-location
				margin-top: 5px
		&-remove
			width: 20px
			height: 20px
			display: flex
			justify-content: center
			align-items: center
			border-radius: 20px
			border: 1px solid #1DB2D1
			position: absolute
			top: 28px
			right: 0px
			svg
				color: #1DB2D1
				width: 10px
	.coupons
		&-text
			&-name
				font-weight: bold

	.progress
		&-bar
			height: 10px
			border-radius: 5px
			background-color: #ccc
			overflow: hidden
			margin-top: 20px
		&-value
			background-color: #27D190
			height: 100%

.user
	&-list
		padding-bottom: 20px
	&-entry
		& + .user-entry
			border-top: 1px solid #f4f4f6
	&-head
		padding: 20px 20px 20px 0
		font-size: 1.25rem
		position: relative
	&-toggle,
	&-remove
		width: 20px
		height: 20px
		display: flex
		justify-content: center
		align-items: center
		border-radius: 20px
		border: 1px solid #1DB2D1
		position: absolute
		top: 24px
		svg
			color: #1DB2D1
			width: 10px
	&-toggle
		right: 0px
	&-remove
		right: 40px
	&-body
		display: none
	&-entry.is-open
		padding-bottom: 20px
		.user-toggle
			transform: rotate(180deg)
		.user-body
			display: block
</style>
